import SidePanel from '@ui/SidePanel/component'
import Slider from '@ui/Slider/component'
import * as coreCommon from 'assets/core/js/common'
import ElementPropertiesManager from 'assets/core/js/module/element-properties-manager'
import YoutubePlayer from 'assets/core/js/module/youtube'
import type { SidePanelType } from '@ui/SidePanel/component'

interface ZoomMediasPanelConfig {
  onInit?(zoom: unknown): void
  onQueryDone?(zoom: unknown): void
  onContentSet?(zoom: unknown): void
}

export default class ZoomMediasPanel {
  id!: string
  element!: HTMLElement
  config!: ZoomMediasPanelConfig
  isOpened!: boolean
  isReady!: boolean
  medias!: Element[]
  videos!: Array<Record<string, unknown>>
  thumbnails!: Element[]
  currentMediaCountEl!: HTMLElement
  activeMediaEl!: Element
  activeThumbnailEl!: Element
  sidePanel!: SidePanelType
  queryXhrRequest!: XMLHttpRequest | null

  constructor(id: string, config?: ZoomMediasPanelConfig) {
    this.config = config ?? {}

    if (!id) {
      return
    }

    this.id = id

    const sidePanelEl = document.getElementById(this.id) as HTMLElement

    if (!sidePanelEl) {
      return
    }

    const sidePanel = SidePanel(sidePanelEl, {
      rootEl: document.body,
      onOpen: () => {
        this.isOpened = true
      },
      onClose: () => {
        if (this.queryXhrRequest && this.queryXhrRequest.readyState < XMLHttpRequest.DONE) {
          this.queryXhrRequest.abort()
          this.isReady = false
        }

        this.isOpened = false
        this.stopAllVideos()
      },
    })

    if (!sidePanel) {
      return
    }

    this.sidePanel = sidePanel
    this.element = this.sidePanel.getElement()
    ElementPropertiesManager.addProperty<ZoomMediasPanel>(sidePanelEl, 'zoomMediasPanel', this)

    this.config.onInit?.(this)
  }

  getElement(): HTMLElement {
    return this.element
  }

  setup(): void {
    this.medias = []
    this.videos = []
    this.thumbnails = []
    const url = this.element.getAttribute('data-url')

    if (url) {
      this.queryXhrRequest = coreCommon.createXhr('GET', url)

      this.queryXhrRequest.onreadystatechange = () => {
        if (
          this.queryXhrRequest &&
          this.queryXhrRequest.readyState === XMLHttpRequest.DONE &&
          this.queryXhrRequest.status === 200 &&
          this.queryXhrRequest.response
        ) {
          this.setContent(this.queryXhrRequest.response as string)
          this.config.onQueryDone?.(this)
          this.isReady = true
        }
      }

      this.queryXhrRequest.send()
    } else {
      this.initContent()
      this.isReady = true
    }
  }

  goToMedia(idx: string | number): void {
    const index = typeof idx === 'string' ? parseInt(idx, 10) : idx
    if (!this.isOpened) {
      this.open()
    }

    this.activeMediaEl = this.medias[index] as Element

    if (this.activeMediaEl && this.activeThumbnailEl) {
      this.activeThumbnailEl.removeAttribute('data-active')
      this.activeThumbnailEl = this.thumbnails[index] as Element

      this.stopAllVideos()

      let videoPlayer: null | YoutubePlayer = null
      for (const video of this.videos) {
        if (index === video.index) {
          videoPlayer = video.player as YoutubePlayer
          break
        }
      }
      if (videoPlayer !== null && 'IntersectionObserver' in window) {
        const handleIntersection: IntersectionObserverCallback = (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return
            }
            observer.unobserve(entry.target)
            videoPlayer?.play()
          })
        }

        // cannot put threshold 1.0 because there is exactness issue with scrollIntoView when doing scroll up
        // which does not trigger full intersection
        const observer = new window.IntersectionObserver(handleIntersection, {
          threshold: 0.9,
          root: this.element.querySelector('.medias'),
        })
        observer.observe(this.activeMediaEl)
      }
      this.activeMediaEl.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      })

      this.activeThumbnailEl.setAttribute('data-active', 'true')
      this.currentMediaCountEl.innerText = (Number(index) + 1).toString()
    }
  }

  open(): void {
    if (!this.isReady) {
      this.setup()
    }
    this.sidePanel.open()
  }

  close(): void {
    this.sidePanel.close()
  }

  setContent(content: string): void {
    this.sidePanel.setContent(content)
    this.initContent()
  }

  private initContent(): void {
    const medias = this.element.querySelectorAll('.medias > *')

    if (!medias || medias.length === 0) {
      return
    }

    const totalEl = this.element.querySelector<HTMLElement>('.total')

    if (totalEl) {
      totalEl.innerText = medias.length.toString()
    }

    this.currentMediaCountEl = this.element.querySelector('.thumbnails-count span') as HTMLElement
    this.currentMediaCountEl.innerText = '1'

    const sliderEl = this.element.querySelector<HTMLElement>('.thumbnails.slider')

    if (sliderEl) {
      Slider(sliderEl, {
        navigation: {
          prevEl: this.element.querySelector<HTMLElement>('.slider-navigation .slider-button-prev'),
          nextEl: this.element.querySelector<HTMLElement>('.slider-navigation .slider-button-next'),
        },
        pagination: {
          el: this.element.querySelector<HTMLElement>('.slider-pagination') ?? null,
        },
      })
    }

    this.element.querySelectorAll('.medias > *').forEach((el) => {
      this.medias.push(el)

      if (el.classList.contains('dca-video-container')) {
        let player: null | YoutubePlayer = null
        if (el.hasAttribute('data-youtube-id')) {
          player = new YoutubePlayer(el, el.getAttribute('data-youtube-id') as string, (player: YoutubePlayer) => this.stopAllVideos(player))
          const initPromise = player.init()
          if (initPromise) {
            initPromise.then(() => true).catch(() => true)
          }
        }
        this.videos.push({
          index: this.medias.length - 1, // relation between the detail of the image/video use index, so we have to keep it
          player,
        })
      }
    })

    if (this.medias[0]) {
      this.activeMediaEl = this.medias[0]
    }

    this.element.querySelectorAll('.thumbnail > *').forEach((el, i) => {
      this.thumbnails.push(el)

      el.addEventListener('click', () => {
        this.goToMedia(i)
      })
    })

    if (this.thumbnails[0]) {
      this.activeThumbnailEl = this.thumbnails[0]
    }

    this.config.onContentSet?.(this)
  }

  stopAllVideos(exceptPlayer?: YoutubePlayer): void {
    this.videos.forEach((video) => {
      if (exceptPlayer !== video.player) {
        video.player && (video.player as YoutubePlayer).stop()
      }
    })
  }
}
